import { useParams, useSearchParams } from 'react-router';

import leavePage from '@/utils/leavePage.ts';
import useDebouncedValue from '@/utils/useDebouncedValue.ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError } from './client.ts';
import {
    useCreateConnection,
    useCreateEmployeeConnection,
    useCreateEmployeeFeed,
    useCreateFeed,
    useDeleteConnection,
    useDeleteEmployeeConnection,
    useDeleteEmployeeFeed,
    useDeleteFeed,
    useListConnections,
    useListEmployeeConnections,
    useListEmployeeProviders,
    useListEmployeeTodoLists,
    useListProviders,
    useListTodoLists,
    usePreviewRouteHook,
    useRenewConnectionToken,
    useRenewEmployeeConnectionToken,
    useRenewEmployeeFeedSecret,
    useRenewFeedSecret,
    useStartEmployeeOauth,
    useStartOauth,
    useSyncEmployeeFeed,
    useSyncFeed,
    useUpdateConnection,
    useUpdateEmployeeConnection,
    useUpdateEmployeeFeed,
    useUpdateFeed,
} from './generated/integrations.ts';
import type { Connection, Facet, Provider, RouteConditionInput } from './generated/schemas.ts';
import { useWorkspace } from './workspaces.ts';

// The endpoints of the connections a page shows: a member's, or a client's employee's, who only has their own
// (plan task 35); the same components show both.
const MEMBER_CONNECTIONS_API = {
    useListProviders,
    useListConnections,
    useCreateConnection,
    useStartOauth,
    useUpdateConnection,
    useDeleteConnection,
    useCreateFeed,
    useUpdateFeed,
    useDeleteFeed,
    useSyncFeed,
    useRenewFeedSecret,
    useRenewConnectionToken,
    useListTodoLists,
};

const EMPLOYEE_CONNECTIONS_API: typeof MEMBER_CONNECTIONS_API = {
    useListProviders: useListEmployeeProviders,
    useListConnections: useListEmployeeConnections,
    useCreateConnection: useCreateEmployeeConnection,
    useStartOauth: useStartEmployeeOauth,
    useUpdateConnection: useUpdateEmployeeConnection,
    useDeleteConnection: useDeleteEmployeeConnection,
    useCreateFeed: useCreateEmployeeFeed,
    useUpdateFeed: useUpdateEmployeeFeed,
    useDeleteFeed: useDeleteEmployeeFeed,
    useSyncFeed: useSyncEmployeeFeed,
    useRenewFeedSecret: useRenewEmployeeFeedSecret,
    useRenewConnectionToken: useRenewEmployeeConnectionToken,
    useListTodoLists: useListEmployeeTodoLists,
};

export const useConnectionsApi = () =>
    useWorkspace().role === 'employee' ? EMPLOYEE_CONNECTIONS_API : MEMBER_CONNECTIONS_API;

// A user's own connection is below their settings, the workspace's below its settings.
export const connectionPath = (connection: Pick<Connection, 'id' | 'is_own'>) =>
    `${connection.is_own ? '/user-settings' : '/settings'}/connections/${connection.id}`;

// The account it logs in as, next to its provider, unless that says nothing more: the connection's own name, or the
// provider's for one without an account (a webhook's).
export const shownAccount = (connection: Pick<Connection, 'name' | 'account' | 'provider_name'>) =>
    connection.account && connection.account !== connection.name && connection.account !== connection.provider_name
        ? connection.account
        : null;

// Whether its feeds are worth counting: an API connection's provider has none, only its token (plan task 69).
export const hasFeeds = (provider: Pick<Provider, 'facets'> | undefined) => (provider?.facets.length ?? 1) > 0;

// The connection of the URL, from the list, which is polled while one of its feeds syncs; undefined while it loads.
// Throws a 404 for one that isn't there or is someone else's own, so the route's error element shows "not found".
export const useConnectionOfPath = () => {
    const { connectionId } = useParams();
    const connections = useConnectionsApi().useListConnections();

    if (connections.error) throw connections.error;
    if (!connections.data) return undefined;
    const connection = connections.data.find((item) => item.id === connectionId);
    if (!connection) throw new ApiError(404, 'not_found', 'Not found');
    return connection;
};

interface SignInOptions {
    providerKey: string;
    isOwn?: boolean;
    // Signing in again to the connection: to clear its error, or to grant its facets' scopes.
    connectionId?: string;
    facets?: Facet[];
    // For picking files, coming back to the Quellen, which open the picker (plan task 39).
    isForFiles?: boolean;
    // Coming back to the Übersicht, whose start it was started from (plan task 124).
    isForStart?: boolean;
}

// Signs in at an OAuth provider's service: the whole page goes there, and the API's callback brings it back to the
// connection's page (plan task 35).
export const useSignIn = () => {
    const startOauth = useConnectionsApi().useStartOauth();

    const signIn = async ({
        providerKey,
        isOwn = false,
        connectionId,
        facets = [],
        isForFiles = false,
        isForStart = false,
    }: SignInOptions) => {
        const { authorize_url } = await startOauth.mutateAsync({
            data: {
                provider: providerKey,
                is_own: isOwn,
                connection_id: connectionId ?? null,
                facets,
                is_for_files: isForFiles,
                is_for_start: isForStart,
            },
        });
        return leavePage(authorize_url);
    };
    return { signIn, isPending: startOauth.isPending || startOauth.isSuccess, error: startOauth.error };
};

// Why a sign-in the API's callback came back from didn't connect (`?connection_error=`), and dropping it once read.
export const useSignInError = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const code = searchParams.get('connection_error');

    const dismiss = () =>
        setSearchParams(
            (params) => {
                params.delete('connection_error');
                return params;
            },
            { replace: true }
        );
    return { code, dismiss };
};

// The provider that reads a link into a source, as the API matches it (plan/DECISIONS.md D82); null for a web page.
export const useLinkProvider = (url: string) => {
    const providers = useListProviders();
    return (
        providers.data?.find((provider) =>
            provider.url_patterns.some((pattern) => new RegExp(`^(?:${pattern})`, 'i').test(url))
        ) ?? null
    );
};

// A rule's groups tried on its feed's last sources while its dialog is open (plan task 122), once typing pauses: which
// match, and where a search query doesn't parse. The last answer stays while the next is fetched; nothing is asked
// while it couldn't tell (`isEnabled`).
export const useRoutePreview = (
    feedId: string,
    routeId: string | null,
    groups: RouteConditionInput[][],
    isEnabled: boolean
) => {
    const previewRoute = usePreviewRouteHook();
    const debouncedGroups = useDebouncedValue(groups, 300);
    return useQuery({
        queryKey: ['routePreview', feedId, routeId, debouncedGroups],
        queryFn: () => previewRoute(feedId, { groups: debouncedGroups, route_id: routeId }),
        placeholderData: keepPreviousData,
        enabled: isEnabled,
    });
};
